'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIMedia from 'UIComponents/layout/UIMedia';
import UIMediaLeft from 'UIComponents/layout/UIMediaLeft';
import UIMediaBody from 'UIComponents/layout/UIMediaBody';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import NetworkIcon from 'AdsUI/components/NetworkIcon';
import UIStatusTag from 'UIComponents/tag/UIStatusTag';
import UILink from 'UIComponents/link/UILink';
import { PermissionsMessage, InactiveMessage } from 'ads-account-connection/components/AccountPermissionsMessaging';
import { AdAccountErrorStates } from 'AdsUI/records/ad-accounts/AdAccountRecord';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import StopPropagationWrapper from './StopPropagationWrapper';
import CurrentUserNotConnected from './CurrentUserNotConnected';
import { Networks, GOOGLE_SUPPORT_BILLING_LINK, FACEBOOK_BUSINESS_PAYMENT_LINK } from 'AdsUI/lib/Constants';
const SETTINGS_URL = `/settings/${PortalIdParser.get()}/marketing/ads/adAccounts`;

const getStatusTag = (message, use = 'danger') => {
  const StatusTagComponent = ({
    children
  }) => /*#__PURE__*/_jsxs("div", {
    className: "m-y-2",
    children: [children, /*#__PURE__*/_jsx(UIStatusTag, {
      use: use,
      className: "display-block",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      })
    })]
  });

  return StatusTagComponent;
};

const onClickError = type => () => {
  usageTracker.track('errorAction', {
    type,
    action: 'clicked kb link'
  });
};

const LONGFORM_ERROR_STATES = [AdAccountErrorStates.MISSING_SCOPES, AdAccountErrorStates.MISSING_MANAGE_SCOPES, AdAccountErrorStates.PAYMENT_METHOD_MISSING];

const WRAPPER_BY_ERROR_STATE = network => ({
  [AdAccountErrorStates.INACTIVE]: getStatusTag('errors.permissions.inactive'),
  [AdAccountErrorStates.NO_PRIMARY_USER]: getStatusTag('multiAuthPermissions.noPrimaryUser'),
  [AdAccountErrorStates.PERMISSIONS_NEEDED]: getStatusTag('errors.permissions.permissionsNeeded'),
  [AdAccountErrorStates.LEAD_AD_PERMISSIONS_NEEDED]: getStatusTag('errors.permissions.permissionsNeeded'),
  [AdAccountErrorStates.CURRENT_USER_HAS_NOT_CONNECTED]: getStatusTag('campaigns.campaignTable.status.ACTIVE', 'active'),
  [AdAccountErrorStates.NOT_AUTHENTICATED]: getStatusTag('errors.permissions.notAuthenticated'),
  [AdAccountErrorStates.MISSING_MANAGE_SCOPES]: getStatusTag('errors.permissions.permissionsNeeded'),
  [AdAccountErrorStates.MISSING_SCOPES]: getStatusTag('errors.permissions.permissionsNeeded'),
  [AdAccountErrorStates.PAYMENT_METHOD_MISSING]: getStatusTag(`errors.leadAdCreation.paymentMethodMissing.${network}.title`),
  [AdAccountErrorStates.NONE]: ({
    children
  }) => children
});

const TOOLTIP_BY_ERROR_STATE = account => ({
  [AdAccountErrorStates.NO_PRIMARY_USER]: /*#__PURE__*/_jsxs("span", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "multiAuthPermissions.reporting"
    }), ' ', /*#__PURE__*/_jsx(UILink, {
      href: SETTINGS_URL,
      external: true,
      children: I18n.text('multiAuthPermissions.goToAdsSettings')
    })]
  }),
  [AdAccountErrorStates.INACTIVE]: /*#__PURE__*/_jsx(InactiveMessage, {
    adAccount: account,
    onClick: onClickError('inactive')
  }),
  [AdAccountErrorStates.PERMISSIONS_NEEDED]: /*#__PURE__*/_jsx(PermissionsMessage, {
    adAccount: account,
    network: account.network,
    onClick: onClickError('permissions')
  }),
  [AdAccountErrorStates.LEAD_AD_PERMISSIONS_NEEDED]: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `errors.leadAdCreation.leadAdPermsNeeded.${account.network}`
  }),
  [AdAccountErrorStates.CURRENT_USER_HAS_NOT_CONNECTED]: /*#__PURE__*/_jsx(CurrentUserNotConnected, {
    network: account.network,
    action: "createAds"
  }),
  [AdAccountErrorStates.NOT_AUTHENTICATED]: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: "errors.permissions.expiredNoName",
    options: {
      settingsUrl: SETTINGS_URL,
      network: I18n.text(account.network.toLowerCase())
    }
  }),
  [AdAccountErrorStates.MISSING_MANAGE_SCOPES]: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `missingScopes.${account.network}`
  }),
  [AdAccountErrorStates.MISSING_SCOPES]: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
    message: `missingScopes.${account.network}`
  }),
  [AdAccountErrorStates.PAYMENT_METHOD_MISSING]: /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: `errors.leadAdCreation.paymentMethodMissing.${account.network}.tooltip_jsx`,
    elements: {
      Link: UILink
    },
    options: {
      external: true,
      href: account.network === Networks.FACEBOOK ? FACEBOOK_BUSINESS_PAYMENT_LINK : account.network === Networks.ADWORDS ? GOOGLE_SUPPORT_BILLING_LINK : null,
      className: 'display-block m-top-1'
    }
  }),
  [AdAccountErrorStates.NONE]: null
});

const AdAccountLabel = ({
  account,
  Wrapper
}) => /*#__PURE__*/_jsxs(UIMedia, {
  align: "center",
  title: account.getDisplayName(),
  children: [/*#__PURE__*/_jsx(UIMediaLeft, {
    children: /*#__PURE__*/_jsx(NetworkIcon, {
      network: account.network,
      size: 24
    })
  }), /*#__PURE__*/_jsx(UIMediaBody, {
    children: /*#__PURE__*/_jsx(Wrapper, {
      children: account.getDisplayName()
    })
  })]
});

const AdAccountCheckbox = ({
  checked,
  disabled,
  onChange,
  children
}) => /*#__PURE__*/_jsx(UICheckbox, {
  checked: checked,
  onChange: onChange,
  disabled: disabled,
  alignment: "center",
  className: "m-x-4",
  children: children
});

const AdAccountDiv = ({
  errorState,
  className,
  children
}) => /*#__PURE__*/_jsx("div", {
  className: `m-x-4 ${className} ${errorState === AdAccountErrorStates.NONE ? 'm-y-1 p-y-2' : 'is-disabled'}`,
  children: children
});

const AdAccountSelectItemLabel = ({
  option: {
    account,
    reachedMax,
    checked,
    onChangeAdAccount,
    showCheckbox,
    errorState,
    disablePaymentMissingAccount
  },
  className
}) => {
  const LabelWrapper = WRAPPER_BY_ERROR_STATE(account.network)[errorState];
  const BoxWrapper = showCheckbox ? AdAccountCheckbox : AdAccountDiv;
  return /*#__PURE__*/_jsx(UITooltip, {
    disabled: errorState === AdAccountErrorStates.NONE && (!reachedMax || checked),
    title: /*#__PURE__*/_jsx(StopPropagationWrapper, {
      children: TOOLTIP_BY_ERROR_STATE(account)[errorState] || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adAccountFilter.maxReached"
      })
    }),
    use: LONGFORM_ERROR_STATES.includes(errorState) ? 'longform' : 'default',
    onOpenChange: e => {
      if (e.target.value && (errorState === AdAccountErrorStates.PERMISSIONS_NEEDED || errorState === AdAccountErrorStates.INACTIVE)) {
        usageTracker.track('errorDisplay', {
          type: errorState === AdAccountErrorStates.PERMISSIONS_NEEDED ? 'permissions' : 'inactive',
          form: 'tooltip'
        });
      }
    },
    children: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(BoxWrapper, {
        checked: checked && (errorState === AdAccountErrorStates.NONE || errorState === AdAccountErrorStates.PAYMENT_METHOD_MISSING),
        onChange: onChangeAdAccount,
        disabled: errorState !== AdAccountErrorStates.NONE && errorState !== AdAccountErrorStates.PAYMENT_METHOD_MISSING || errorState === AdAccountErrorStates.PAYMENT_METHOD_MISSING && disablePaymentMissingAccount || reachedMax && !checked,
        className: className,
        errorState: errorState,
        children: /*#__PURE__*/_jsx(AdAccountLabel, {
          account: account,
          Wrapper: LabelWrapper
        })
      })
    })
  });
};

export default AdAccountSelectItemLabel;